import React, { useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { IFormItemType } from '@interfaces/FormItemType';
import Select from 'react-select';
import { useTranslate } from '@translations/useTranslate';
import { Translations } from '@translations/translationsTypes';
import { CustomInputFeedback } from '../CustomInputFeedback';
import SelectStyles from './CustomSelect.module.scss';

type Props = {
  formItem: Omit<IFormItemType, 'type'>;
  isSmallLabel?: boolean;
};

export const CustomSelect: React.FC<Props> = ({ formItem, children, isSmallLabel = false }) => {
  const { language } = useTranslate();
  const currentValue = useMemo(() => {
    const currentOption = formItem.options?.find((item) => item.value === formItem.value);
    return currentOption
      ? {
          label:
            language === Translations.ar
              ? currentOption.labelAr ?? currentOption.label
              : currentOption.label,
          value: currentOption.value,
        }
      : undefined;
  }, [formItem, language]);
  const options = useMemo(() => {
    return formItem.options?.map((item) => ({
      label: language === Translations.ar ? item.labelAr ?? item.label : item.label,
      value: item.value,
    }));
  }, [language, formItem.options]);
  return (
    <Form.Group className={SelectStyles.form_group} controlId={formItem.id}>
      <Form.Label
        dir="auto"
        className={
          !isSmallLabel ? SelectStyles.form_group__label : SelectStyles.form_group__label_small
        }
      >
        {formItem.required && <span className={SelectStyles.form_group__label_required}>*</span>}{' '}
        {formItem.label} {formItem.isTouched}
      </Form.Label>
      <Form.Control
        as="div"
        className="w-auto p-0"
        style={{ borderRadius: '3px' }}
        isInvalid={!formItem.isValid && formItem.isTouched}
      >
        <Select<{ label: string; value: string | number }>
          className={SelectStyles.form_group__input}
          classNames={{
            control: () => SelectStyles.form_group__input_control,
            placeholder: () => SelectStyles.form_group__input_placeholder,
            indicatorSeparator: () => SelectStyles.form_group__input_separator,
          }}
          name={formItem.id}
          placeholder={formItem.placeholder}
          onChange={(newValue) => {
            if (newValue) {
              formItem.onChange(newValue.value);
            }
          }}
          onBlur={formItem.onBlur}
          isDisabled={formItem.disabled}
          value={currentValue}
          options={options}
          theme={(theme) => ({
            ...theme,
            borderRadius: 3,
            colors: {
              ...theme.colors,
              primary: '#FC4C02',
              primary25: 'rgba(252,76,2,0.2)',
            },
          })}
        />
      </Form.Control>
      <div
        className="w-100 d-flex justify-content-between align-items-start"
        style={{ gap: '5px' }}
      >
        {formItem.note && (
          <p dir="auto" style={{ color: '#0099A8', fontSize: '12px', margin: 0 }}>
            {formItem.note}
          </p>
        )}
      </div>
      {formItem.errorFeedback && !formItem.isValid && formItem.isTouched && (
        <CustomInputFeedback errorFeedback={formItem.errorFeedback} />
      )}
    </Form.Group>
  );
};
